import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { LogOut, Trash2, Download, ChevronDown } from 'lucide-react';
import usePageMeta from './hooks/usePageMeta';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  'Cadastrar',
  'Igreen Feito',
  'Igreen Não',
  'AP Cadastrar',
  'AP Pronto'
];

function AdminIgreen() {
  usePageMeta("AP - Finanças Pessoais", "ap");
  
  const navigate = useNavigate();
  const [cadastros, setCadastros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    checkAuth();
    loadCadastros();
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('admin_token');
    const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
    if (!token || !user.is_admin) {
      navigate('/apadminlogin');
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { headers: { 'Authorization': `Bearer ${token}` } };
  };

  const loadCadastros = async () => {
    try {
      const response = await axios.get(`${API}/cadastros-igreen-ap`, getAuthHeaders());
      // Add default status if not present
      const data = response.data.map(c => ({
        ...c,
        status: c.status || 'Cadastrar'
      }));
      setCadastros(data);
    } catch (error) {
      toast.error('Erro ao carregar cadastros');
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/apadminlogin');
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(cadastros.map(c => c.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      toast.error('Selecione pelo menos um registro');
      return;
    }

    if (!window.confirm(`Tem certeza que deseja excluir ${selectedIds.length} registro(s)? Esta ação é irreversível!`)) {
      return;
    }

    try {
      await axios.post(`${API}/cadastros-igreen-ap/delete-multiple`, { ids: selectedIds }, getAuthHeaders());
      toast.success(`${selectedIds.length} registro(s) excluído(s) com sucesso`);
      setSelectedIds([]);
      loadCadastros();
    } catch (error) {
      toast.error('Erro ao excluir registros');
    }
  };

  const handleStatusChange = async (cadastroId, newStatus) => {
    try {
      await axios.put(`${API}/cadastros-igreen-ap/${cadastroId}/status`, { status: newStatus }, getAuthHeaders());
      setCadastros(prev => prev.map(c => c.id === cadastroId ? { ...c, status: newStatus } : c));
      toast.success('Status atualizado');
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const downloadFile = (base64Data, fileNameBase) => {
    if (!base64Data) {
      toast.error('Arquivo não disponível');
      return;
    }

    try {
      // Handle data URL format
      let base64 = base64Data;
      let mimeType = 'application/octet-stream';
      let extension = '';
      
      if (base64Data.includes(',')) {
        const parts = base64Data.split(',');
        base64 = parts[1];
        const mimeMatch = parts[0].match(/data:([^;]+)/);
        if (mimeMatch) {
          mimeType = mimeMatch[1];
          // Determine extension based on MIME type
          const mimeToExt = {
            'application/pdf': '.pdf',
            'image/jpeg': '.jpg',
            'image/jpg': '.jpg',
            'image/png': '.png',
            'image/gif': '.gif',
            'image/webp': '.webp'
          };
          extension = mimeToExt[mimeType] || '';
        }
      }

      // Remove any existing extension from fileNameBase and add correct one
      const baseWithoutExt = fileNameBase.replace(/\.[^/.]+$/, '');
      const fileName = baseWithoutExt + extension;

      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar arquivo');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Cadastrar': return 'bg-yellow-100 text-yellow-800';
      case 'Igreen Feito': return 'bg-green-100 text-green-800';
      case 'Igreen Não': return 'bg-red-100 text-red-800';
      case 'AP Cadastrar': return 'bg-blue-100 text-blue-800';
      case 'AP Pronto': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Menu */}
      <nav className="bg-white shadow-md">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/apadmin')}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
              >
                AP
              </button>
              <button
                onClick={() => navigate('/apadmin/igreen-publico')}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
              >
                Igreen
              </button>
              <button
                onClick={() => navigate('/apadmin/igreen')}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
              >
                AP-Igreen
              </button>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sair
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Cadastros Igreen AP</h1>
            <p className="text-gray-600 mt-1">Dados enviados via /igreencadastroap</p>
          </div>
          {selectedIds.length > 0 && (
            <button
              onClick={handleDeleteSelected}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4" />
              Excluir ({selectedIds.length})
            </button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedIds.length === cadastros.length && cadastros.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Tipo</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Nome</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">E-mail</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">WhatsApp</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">CNPJ</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Documentos</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Data de Envio</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="9" className="px-3 py-8 text-center text-gray-500">
                      Carregando cadastros...
                    </td>
                  </tr>
                ) : cadastros.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-3 py-8 text-center text-gray-500">
                      Nenhum cadastro encontrado
                    </td>
                  </tr>
                ) : (
                  cadastros.map((cadastro) => (
                    <tr key={cadastro.id} className="hover:bg-gray-50">
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(cadastro.id)}
                          onChange={() => handleSelectOne(cadastro.id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${
                          cadastro.tipo_pessoa === 'CPF' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-orange-100 text-orange-800'
                        }`}>
                          {cadastro.tipo_pessoa}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-gray-900 max-w-[200px] truncate" title={cadastro.nome}>
                        {cadastro.nome}
                      </td>
                      <td className="px-3 py-3 text-gray-600 max-w-[200px] truncate" title={cadastro.email}>
                        {cadastro.email}
                      </td>
                      <td className="px-3 py-3 text-gray-600">
                        {cadastro.whatsapp}
                      </td>
                      <td className="px-3 py-3 text-gray-600">
                        {cadastro.cnpj || '-'}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-1">
                          {cadastro.documento_frente && (
                            <button
                              onClick={() => downloadFile(cadastro.documento_frente, `${cadastro.nome}_doc_frente.jpg`)}
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                              title={cadastro.tipo_pessoa === 'CPF' ? 'RG/CPF Frente' : 'CPF/CNH Responsável Frente'}
                            >
                              <Download className="w-3 h-3" />
                              Frente
                            </button>
                          )}
                          {cadastro.documento_verso && (
                            <button
                              onClick={() => downloadFile(cadastro.documento_verso, `${cadastro.nome}_doc_verso.jpg`)}
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                              title={cadastro.tipo_pessoa === 'CPF' ? 'RG/CPF Verso' : 'CPF/CNH Responsável Verso'}
                            >
                              <Download className="w-3 h-3" />
                              Verso
                            </button>
                          )}
                          {cadastro.conta_luz && (
                            <button
                              onClick={() => downloadFile(cadastro.conta_luz, `${cadastro.nome}_conta_luz.jpg`)}
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                              title="Conta de Luz"
                            >
                              <Download className="w-3 h-3" />
                              Luz
                            </button>
                          )}
                          {cadastro.contrato_social && (
                            <button
                              onClick={() => downloadFile(cadastro.contrato_social, `${cadastro.nome}_contrato.pdf`)}
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                              title="Contrato Social"
                            >
                              <Download className="w-3 h-3" />
                              Contrato
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-gray-600 whitespace-nowrap">
                        {formatDate(cadastro.created_at)}
                      </td>
                      <td className="px-3 py-3">
                        <div className="relative">
                          <select
                            value={cadastro.status}
                            onChange={(e) => handleStatusChange(cadastro.id, e.target.value)}
                            className={`appearance-none pr-8 pl-3 py-1 rounded text-xs font-semibold border-0 cursor-pointer ${getStatusColor(cadastro.status)}`}
                          >
                            {STATUS_OPTIONS.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-3 h-3 pointer-events-none" />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary */}
        {cadastros.length > 0 && (
          <div className="mt-4 text-sm text-gray-500">
            Total: {cadastros.length} cadastro(s) | 
            CPF: {cadastros.filter(c => c.tipo_pessoa === 'CPF').length} | 
            CNPJ: {cadastros.filter(c => c.tipo_pessoa === 'CNPJ').length}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminIgreen;
